/**
 * "How Guardian talks to you" (D5, workspace docs/guardian/03 §3.4,
 * 2026-10-22): the member's own learned answer style. They can see what
 * Guardian has picked up, set any part themselves (which locks it so learning
 * stops changing it), unlock a part to let learning resume, and turn the whole
 * thing off. Plain wording, no jargon. A 503 `style_unavailable` hides the
 * section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { fetchMyStyle, updateMyStyle } from '../api/guardianApi';

// Each feature: the PUT key, a plain label, and the choices (stored value ->
// plain label). Formality is a 0..1 number; the rest are enums.
const FEATURES = [
  {
    key: 'brevity',
    label: 'Answer length',
    options: [['short', 'Short'], ['medium', 'Medium'], ['long', 'Detailed']],
  },
  {
    key: 'format',
    label: 'Layout',
    options: [['bullets', 'Bullet points'], ['prose', 'Paragraphs'], ['mixed', 'A mix']],
  },
  {
    key: 'units',
    label: 'Units',
    options: [['imperial', 'Feet and miles'], ['metric', 'Meters and kilometers']],
  },
  {
    key: 'codes',
    label: 'Radio codes',
    options: [['uses', 'Use codes'], ['avoids', 'Spell things out']],
  },
  {
    key: 'formality',
    label: 'Tone',
    options: [[0, 'Casual'], [0.5, 'Neutral'], [1, 'Formal']],
    numeric: true,
  },
];

export function optionLabel(feature, value) {
  if (value === undefined || value === null) return null;
  const match = feature.options.find(([v]) => v === value);
  if (match) return match[1];
  if (feature.numeric) {
    // Nearest labelled step for a learned fractional value.
    const nearest = feature.options.reduce((best, opt) => (
      Math.abs(opt[0] - value) < Math.abs(best[0] - value) ? opt : best
    ));
    return nearest[1];
  }
  return String(value);
}

function FeatureRow({ feature, value, locked, disabled, onSet, onUnlock }) {
  const current = optionLabel(feature, value);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Typography variant="body2" sx={{ minWidth: 120 }}>{feature.label}</Typography>
      <Select
        size="small"
        displayEmpty
        value={value === undefined || value === null ? '' : value}
        disabled={disabled}
        onChange={(e) => onSet(feature, feature.numeric ? Number(e.target.value) : e.target.value)}
        inputProps={{ 'aria-label': feature.label }}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="" disabled>{current ? `Learned: ${current}` : 'Not learned yet'}</MenuItem>
        {feature.options.map(([v, label]) => (
          <MenuItem key={String(v)} value={v}>{label}</MenuItem>
        ))}
      </Select>
      {locked ? (
        <>
          <Chip size="small" label="You set this" />
          <Button size="small" disabled={disabled} onClick={() => onUnlock(feature)}>Let Guardian learn again</Button>
        </>
      ) : (
        current && <Chip size="small" variant="outlined" label="Learned" />
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 */
export default function GuardianStyleSection({ authToken }) {
  const [style, setStyle] = useState(null);
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const apply = (data) => {
    setStyle({
      enabled: Boolean(data?.enabled),
      features: data?.features && typeof data.features === 'object' ? data.features : {},
      locked: Array.isArray(data?.locked) ? data.locked : [],
      observations: typeof data?.observations === 'string' ? data.observations : '',
    });
  };

  const load = useCallback(async () => {
    const res = await fetchMyStyle(authToken);
    if (!alive.current) return;
    if (res?.success) apply(res.data);
    else if (res?.data?.code === 'style_unavailable') setOff(true);
    else setError(res?.message || 'Could not load your style settings.');
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const send = async (changes) => {
    setBusy(true);
    setError(null);
    const res = await updateMyStyle(authToken, changes);
    setBusy(false);
    if (!res?.success) {
      setError(res?.message || 'Could not save your style settings.');
      return;
    }
    if (res.data) apply(res.data);
    else load();
  };

  if (off) return null;
  if (!authToken) return null;

  const locked = new Set(style?.locked || []);

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 2 }}>
      <Typography variant="overline" color="text.secondary">How Guardian talks to you</Typography>
      {error && <Alert severity="error" sx={{ my: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {style && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
            Guardian can match how you like answers written. Set anything yourself to keep it fixed, or let
            Guardian keep learning.
          </Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={style.enabled}
                disabled={busy}
                onChange={(e) => send({ enabled: e.target.checked })}
                inputProps={{ 'aria-label': 'Let Guardian match your style' }}
              />
            )}
            label="Let Guardian match your style"
          />
          {style.enabled && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {FEATURES.map((feature) => (
                  <FeatureRow
                    key={feature.key}
                    feature={feature}
                    value={style.features[feature.key]}
                    locked={locked.has(feature.key)}
                    disabled={busy}
                    onSet={(f, v) => send({ features: { [f.key]: v } })}
                    onUnlock={(f) => send({ unlock: [f.key] })}
                  />
                ))}
              </Box>
              {style.observations && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  {style.observations}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Paper>
  );
}
